import React from 'react'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePricingCampaignQuery } from '@/entities/pricing-campaign'

import { ActivityView } from '@/widgets/pricing-campaign/activity'
import { AnchoringRulesView } from '@/widgets/pricing-campaign/anchoring'
import { AssignmentView } from '@/widgets/pricing-campaign/assignment'
import { MinimalPriceStepView } from '@/widgets/pricing-campaign/minimal-steps'
import { PriceLimitsView } from '@/widgets/pricing-campaign/price-limits'
import { PricingTacticsView } from '@/widgets/pricing-campaign/pricing-tactics'
import { RoundingRulesView } from '@/widgets/pricing-campaign/rounding-rules'

import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'

export const PricingCampaignPanel = ({
	pricingCampaignId,
}: {
	pricingCampaignId: string
}) => {
	const { t } = useTranslation()

	const pricingCampaignsQuery = usePricingCampaignQuery(pricingCampaignId, {
		enabled: !!pricingCampaignId,
	})
	if (pricingCampaignsQuery.isLoading) {
		return <PricingCampaignPanelSkeleton />
	}
	if (pricingCampaignsQuery.isError) {
		return (
			<div className='w-full p-4'>
				<Alert variant='destructive'>
					<AlertTriangleIcon />
					<AlertTitle>
						{t('app.unexpected_error', { defaultValue: 'Unexpected error' })}
					</AlertTitle>
					<AlertDescription>
						{t('app.pricing campaign_fetch_error', {
							defaultValue:
								'Something went wrong while fetching the pricing campaign, please try again later or contact support.',
						})}
					</AlertDescription>
				</Alert>
			</div>
		)
	}
	if (!pricingCampaignsQuery.data) {
		return null
	}

	return (
		<div className='flex flex-1 flex-col gap-4 overflow-hidden overflow-y-auto px-4 pb-4'>
			<ActivityView settings={pricingCampaignsQuery.data.settings?.activity} />
			<AssignmentView
				assignments={pricingCampaignsQuery.data.settings?.product_assignments}
			/>
			<PricingTacticsView
				settings={pricingCampaignsQuery.data.settings?.pricing_tactics}
			/>
			<PriceLimitsView
				settings={pricingCampaignsQuery.data.settings?.price_limits}
			/>
			<AnchoringRulesView
				settings={pricingCampaignsQuery.data.settings?.anchoring}
			/>
			<RoundingRulesView
				settings={pricingCampaignsQuery.data.settings?.rounding}
			/>
			<MinimalPriceStepView
				settings={pricingCampaignsQuery.data.settings.minimal_steps}
			/>
		</div>
	)
}
const PricingCampaignPanelSkeleton = () => (
	<div className='flex h-[calc(100%-40px)] w-full flex-col gap-2 p-4'>
		<Skeleton className='h-96 w-full' />
		<Skeleton className='h-32 w-full' />
		<Skeleton className='h-56 w-full' />
		<Skeleton className='h-24 w-full' />
	</div>
)
